import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { 
    Users, 
    Building2, 
    Calendar, 
    FileSpreadsheet, 
    Crown,
    Megaphone,
    TrendingUp,
    UserPlus,
    Shield,
    AlertTriangle,
    Gift,
    ShoppingBag,
    CircleDollarSign,
    Clock3,
    NotebookPen,
    PhoneCall,
    ExternalLink
} from 'lucide-react';
import './Admin.css';
import { ApiError } from '../../../../lib/apiClient';
import { adminService } from '../../../../services';
import { appConfig } from '../../../config/appConfig';
import { getClubPublicPath } from '../../../../utils/clubPublicPaths';

const AdminDashboard = () => {
    const { user } = useUser();
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [crmFilter, setCrmFilter] = useState('all');

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            setIsLoading(true);
            const data = await adminService.getStats();
            setStats(data);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setError('You do not have admin access');
                return;
            }
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDateTime = (value) => {
        if (!value) return 'Not set';
        try {
            return new Date(value).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return value;
        }
    };

    const venueCrmSummary = stats?.venueCrmSummary;
    const recentVenueNotes = stats?.recentVenueNotes || [];
    const filteredVenuePipeline = useMemo(() => {
        const venuePipeline = stats?.venuePipeline || [];
        const now = new Date();
        const weekFromNow = new Date(now);
        weekFromNow.setDate(weekFromNow.getDate() + 7);

        return venuePipeline.filter((venue) => {
            const nextFollowUpAt = venue.nextFollowUpAt ? new Date(venue.nextFollowUpAt) : null;

            switch (crmFilter) {
                case 'due-now':
                    return nextFollowUpAt && nextFollowUpAt <= now;
                case 'premium':
                    return venue.isPremiumVenue;
                case 'no-notes':
                    return !venue.noteCount;
                case 'due-week':
                    return nextFollowUpAt && nextFollowUpAt >= now && nextFollowUpAt <= weekFromNow;
                default:
                    return true;
            }
        });
    }, [crmFilter, stats?.venuePipeline]);

    if (!user?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access the admin panel.</p>
                    <Link to="/dashboard/home" className="admin-btn admin-btn-primary">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="admin-page">
                <div className="admin-loading">
                    <div className="admin-spinner"></div>
                    <p>Loading admin dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page">
                <div className="admin-error">
                    <AlertTriangle size={48} />
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={fetchStats} className="admin-btn admin-btn-primary">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <div className="admin-header-icon">
                        <Shield size={28} />
                    </div>
                    <div>
                        <h1>Admin Dashboard</h1>
                        <p>Manage {appConfig.appDisplayName} platform settings and users</p>
                    </div>
                </div>
            </div>

            <div className="admin-overview-strip">
                <div className="admin-overview-card">
                    <div className="admin-overview-label">Platform Growth</div>
                    <strong>{stats?.newUsersThisMonth || 0}</strong>
                    <p>new users this month, with {stats?.activeUsers || 0} active in the last 30 days.</p>
                </div>
                <div className="admin-overview-card">
                    <div className="admin-overview-label">Commercial Snapshot</div>
                    <strong>{stats?.premiumSubscribers || 0}</strong>
                    <p>premium members, {stats?.activeBanners || 0} active banners, {stats?.totalEvents || 0} total events.</p>
                </div>
                <div className="admin-overview-card admin-overview-card-venue">
                    <div className="admin-overview-label">Venue CRM Snapshot</div>
                    <strong>{venueCrmSummary?.premiumVenues || 0}</strong>
                    <p>premium venues, {venueCrmSummary?.followUpsDueNow || 0} follow-ups due now, {venueCrmSummary?.venuesContactedThisWeek || 0} contacted this week.</p>
                </div>
                <div className="admin-overview-links">
                    <Link to="/dashboard/admin/users" className="admin-overview-link">Open User Admin</Link>
                    <Link to="/dashboard/admin/banners" className="admin-overview-link">Open Banner Admin</Link>
                    <Link to="/dashboard/admin/equipment" className="admin-overview-link">Open Equipment Data</Link>
                    <Link to="/dashboard/admin/perks" className="admin-overview-link">Open Perks Admin</Link>
                    <Link to="/dashboard/admin/marketplace" className="admin-overview-link">Open Marketplace Admin</Link>
                    <Link to="/dashboard/manage-clubs" className="admin-overview-link">Open Venue Admin</Link>
                </div>
            </div>

            <div className="admin-stats-grid">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon blue">
                        <Users size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.totalUsers || 0}</h3>
                        <p>Total Users</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon green">
                        <TrendingUp size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.activeUsers || 0}</h3>
                        <p>Active Users (30d)</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon purple">
                        <UserPlus size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.newUsersThisMonth || 0}</h3>
                        <p>New This Month</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon orange">
                        <Crown size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.premiumSubscribers || 0}</h3>
                        <p>Premium Members</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon teal">
                        <Building2 size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.totalClubs || 0}</h3>
                        <p>Total {appConfig.terminology.venuePlural.charAt(0).toUpperCase() + appConfig.terminology.venuePlural.slice(1)}</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon red">
                        <Calendar size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.totalEvents || 0}</h3>
                        <p>Total Events</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon indigo">
                        <FileSpreadsheet size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.totalScoreSheets || 0}</h3>
                        <p>Score Sheets</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon yellow">
                        <Megaphone size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{stats?.activeBanners || 0}</h3>
                        <p>Active Banners</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon emerald">
                        <Building2 size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{venueCrmSummary?.premiumVenues || 0}</h3>
                        <p>Premium Venues</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon slate">
                        <CircleDollarSign size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{venueCrmSummary?.freeVenues || 0}</h3>
                        <p>Free Venues</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon rose">
                        <Clock3 size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{venueCrmSummary?.followUpsDueNow || 0}</h3>
                        <p>Follow-Ups Due</p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon cyan">
                        <PhoneCall size={24} />
                    </div>
                    <div className="admin-stat-content">
                        <h3>{venueCrmSummary?.venuesContactedThisWeek || 0}</h3>
                        <p>Contacted This Week</p>
                    </div>
                </div>
            </div>

            <div className="admin-crm-section">
                <div className="admin-crm-header">
                    <div>
                        <h2>Venue CRM</h2>
                        <p>Track premium conversion, follow-up priorities, and the latest staff activity across venue relationships.</p>
                    </div>
                    <Link to="/dashboard/manage-clubs" className="admin-btn admin-btn-secondary">
                        Open Venue Admin
                    </Link>
                </div>

                <div className="admin-crm-summary-grid">
                    <div className="admin-crm-summary-card">
                        <span>CRM-active venues</span>
                        <strong>{venueCrmSummary?.venuesWithCrmActivity || 0}</strong>
                        <p>Venues with notes, follow-ups, premium tracking, or staff summaries.</p>
                    </div>
                    <div className="admin-crm-summary-card">
                        <span>Due this week</span>
                        <strong>{venueCrmSummary?.followUpsDueThisWeek || 0}</strong>
                        <p>Venues with follow-ups scheduled in the next 7 days.</p>
                    </div>
                    <div className="admin-crm-summary-card">
                        <span>Shopfront-ready venues</span>
                        <strong>{venueCrmSummary?.venuesWithMarketplaceListings || 0}</strong>
                        <p>Venues already running active marketplace listings.</p>
                    </div>
                    <div className="admin-crm-summary-card">
                        <span>Perks live</span>
                        <strong>{venueCrmSummary?.venuesWithPerks || 0}</strong>
                        <p>Venues with active offers or perks already visible in-platform.</p>
                    </div>
                </div>

                <div className="admin-crm-grid">
                    <section className="admin-crm-panel">
                        <div className="admin-crm-panel-heading">
                            <NotebookPen size={18} />
                            <h3>Pipeline & Follow-Ups</h3>
                        </div>
                        <div className="admin-crm-venue-badges" style={{ marginBottom: '1rem' }}>
                            <button type="button" className={`admin-crm-badge ${crmFilter === 'all' ? 'premium' : 'neutral'}`} onClick={() => setCrmFilter('all')}>
                                All
                            </button>
                            <button type="button" className={`admin-crm-badge ${crmFilter === 'due-now' ? 'premium' : 'neutral'}`} onClick={() => setCrmFilter('due-now')}>
                                Due Now
                            </button>
                            <button type="button" className={`admin-crm-badge ${crmFilter === 'due-week' ? 'premium' : 'neutral'}`} onClick={() => setCrmFilter('due-week')}>
                                Due This Week
                            </button>
                            <button type="button" className={`admin-crm-badge ${crmFilter === 'premium' ? 'premium' : 'neutral'}`} onClick={() => setCrmFilter('premium')}>
                                Premium
                            </button>
                            <button type="button" className={`admin-crm-badge ${crmFilter === 'no-notes' ? 'premium' : 'neutral'}`} onClick={() => setCrmFilter('no-notes')}>
                                No Notes
                            </button>
                        </div>
                        {filteredVenuePipeline.length === 0 ? (
                            <p className="admin-crm-empty">No venue pipeline activity yet.</p>
                        ) : (
                            <div className="admin-crm-venue-list">
                                {filteredVenuePipeline.map((venue) => (
                                    <article key={venue.clubId} className="admin-crm-venue-card">
                                        <div className="admin-crm-venue-top">
                                            <div>
                                                <h4>{venue.name}</h4>
                                                <div className="admin-crm-venue-badges">
                                                    <span className={`admin-crm-badge ${venue.isPremiumVenue ? 'premium' : 'free'}`}>
                                                        {venue.isPremiumVenue ? 'Premium Venue' : 'Free Venue'}
                                                    </span>
                                                    <span className="admin-crm-badge neutral">{venue.stage || 'Prospect'}</span>
                                                </div>
                                            </div>
                                            <div className="admin-crm-venue-links">
                                                <Link to={`/dashboard/club/${venue.clubId}/plan#shoot-connect-crm`} className="admin-crm-link">
                                                    Open CRM
                                                </Link>
                                                <a href={getClubPublicPath(venue.publicKey)} target="_blank" rel="noopener noreferrer" className="admin-crm-link">
                                                    Public View
                                                </a>
                                            </div>
                                        </div>
                                        <div className="admin-crm-venue-meta">
                                            <span>Next follow-up: {formatDateTime(venue.nextFollowUpAt)}</span>
                                            <span>Last contacted: {formatDateTime(venue.lastContactedAt)}</span>
                                            <span>Method: {venue.lastContactMethod || 'Not set'}</span>
                                        </div>
                                        <p>{venue.summary || 'No summary captured yet.'}</p>
                                        <div className="admin-crm-venue-meta">
                                            <span>{venue.noteCount || 0} notes</span>
                                            <span>{venue.activeOfferCount || 0} perks live</span>
                                            <span>{venue.activeListingCount || 0} listings live</span>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="admin-crm-panel">
                        <div className="admin-crm-panel-heading">
                            <Clock3 size={18} />
                            <h3>Recent Venue Notes</h3>
                        </div>
                        {recentVenueNotes.length === 0 ? (
                            <p className="admin-crm-empty">No staff notes recorded yet.</p>
                        ) : (
                            <div className="admin-crm-note-feed">
                                {recentVenueNotes.map((note) => (
                                    <article key={note.clubAdminCrmNoteId} className="admin-crm-note-item">
                                        <div className="admin-crm-note-head">
                                            <div>
                                                <strong>{note.clubName}</strong>
                                                <div className="admin-crm-venue-badges">
                                                    <span className={`admin-crm-badge ${note.isPremiumVenue ? 'premium' : 'free'}`}>
                                                        {note.isPremiumVenue ? 'Premium' : 'Free'}
                                                    </span>
                                                    {note.stageAtTime ? <span className="admin-crm-badge neutral">{note.stageAtTime}</span> : null}
                                                </div>
                                            </div>
                                            <Link to={`/dashboard/club/${note.clubId}/plan#shoot-connect-crm`} className="admin-crm-link">
                                                <ExternalLink size={14} />
                                                Open
                                            </Link>
                                        </div>
                                        <p>{note.note}</p>
                                        <div className="admin-crm-note-meta">
                                            <span>{formatDateTime(note.occurredAt)}</span>
                                            <span>{note.contactMethod || 'Internal note'}</span>
                                            <span>By {note.createdByName || 'Shoot Connect Admin'}</span>
                                            {note.followUpAt ? <span>Follow-up {formatDateTime(note.followUpAt)}</span> : null}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>

            <div className="admin-quick-actions">
                <h2>Quick Actions</h2>
                <div className="admin-actions-grid">
                    <Link to="/dashboard/admin/banners" className="admin-action-card">
                        <div className="admin-action-icon">
                            <Megaphone size={32} />
                        </div>
                        <h3>Manage Banners</h3>
                        <p>Create and manage site-wide announcements</p>
                    </Link>

                    <Link to="/dashboard/admin/users" className="admin-action-card">
                        <div className="admin-action-icon">
                            <Users size={32} />
                        </div>
                        <h3>Manage Users</h3>
                        <p>View, edit, and manage user accounts</p>
                    </Link>

                    <Link to="/dashboard/clubs" className="admin-action-card">
                        <div className="admin-action-icon">
                            <Building2 size={32} />
                        </div>
                        <h3>View All {appConfig.terminology.venuePlural.charAt(0).toUpperCase() + appConfig.terminology.venuePlural.slice(1)}</h3>
                        <p>Browse and manage all registered {appConfig.terminology.venuePlural}</p>
                    </Link>

                    <Link to="/dashboard/admin/equipment" className="admin-action-card">
                        <div className="admin-action-icon">
                            <FileSpreadsheet size={32} />
                        </div>
                        <h3>Manage Equipment Data</h3>
                        <p>Add and edit categories, brands, and specification definitions.</p>
                    </Link>

                    <Link to="/dashboard/admin/perks" className="admin-action-card">
                        <div className="admin-action-icon">
                            <Gift size={32} />
                        </div>
                        <h3>Manage Venue Perks</h3>
                        <p>Review all perks, toggle live/featured status, and moderate offers platform-wide.</p>
                    </Link>

                    <Link to="/dashboard/admin/marketplace" className="admin-action-card">
                        <div className="admin-action-icon">
                            <ShoppingBag size={32} />
                        </div>
                        <h3>Moderate Marketplace</h3>
                        <p>Review listings and offers, adjust statuses, and remove problematic content.</p>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
